import { useQuery } from '@apollo/client';
import isPropValid from '@emotion/is-prop-valid';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  useTheme,
} from '@mui/material';
import { Box, styled } from '@mui/system';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { BaseTypography } from '../../../base/components/BaseTypography';
import { Flex } from '../../../base/components/Flex';
import ImagePlaceholder from '../../../base/components/ImagePlaceholder/ImagePlaceholder';
import QrSceneCard from '../../../client/pages/LaunchCompanyQrPage/components/QrSceneCard';
import {
  EditApplicationSceneDocument,
  GetApplicationSceneDocument,
  GetApplicationSceneQuery,
  SetSceneParameterValueInput,
} from '../../../graphql/graphql-operations';
import { useMutationWithSnackbar } from '../../../hooks/useMutationWithSnackbar';
import useQueryErrorSnackbar from '../../../hooks/useQueryErrorSnackbar';
import { compareVersions } from '../../../lib/versionComparator';
import { SectionPaper } from '../../components/SectionPaper';
import { TopBar } from '../../components/TopBar';
import ApplicationSceneEditDialog from './ApplicationSceneEditDialog';
import ApplicationSceneManualSection from './ApplicationSceneManualSection';
import ApplicationVersionSelector from './ApplicationVersionSelector';

type Parameter = NonNullable<
  GetApplicationSceneQuery['applicationScene']['parameters']
>[0];
interface CheckedParam {
  [parameterId: string]: { [valueId: string]: boolean };
}

const ApplicationSceneDetailPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { applicationSceneId } = useParams<{ applicationSceneId: string }>();
  const { t } = useTranslation();
  const [selectedVersionId, setSelectedVersionId] = useState<string>();
  const [checkedParams, setCheckedParams] = useState<CheckedParam>({});
  const [edited, setEdited] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [params, setParams] = useSearchParams();

  const { data, loading, error, refetch } = useQuery(
    GetApplicationSceneDocument,
    {
      variables: {
        applicationSceneId: applicationSceneId!,
        applicationVersionId: selectedVersionId,
      },
      skip: !applicationSceneId,
    },
  );
  useQueryErrorSnackbar(error, refetch);
  useEffect(() => {
    if (applicationSceneId && selectedVersionId) {
      refetch({
        applicationSceneId: applicationSceneId,
        applicationVersionId: selectedVersionId,
      });
    }
  }, [applicationSceneId, selectedVersionId, refetch]);

  useEffect(() => {
    if (
      !selectedVersionId &&
      data?.applicationScene.applicationVersions?.length
    ) {
      const sortedVersions = [
        ...data.applicationScene.applicationVersions,
      ].sort(compareVersions('desc'));
      params.set('applicationVersionId', sortedVersions[0].id);
      navigate({ search: params.toString() });
    }
  }, [params, setParams, data?.applicationScene.applicationVersions]);

  const scene = data?.applicationScene;
  const enabled = !!scene?.enabled;

  const [editMutation] = useMutationWithSnackbar(
    'applications.scene.editApplicationScene',
    EditApplicationSceneDocument,
  );

  const prepareSceneParamsForSave = (
    checkedParams: CheckedParam,
  ): SetSceneParameterValueInput[] => {
    const result: SetSceneParameterValueInput[] = [];
    for (const paramId in checkedParams) {
      const paramValues = checkedParams[paramId];
      for (const valueId in paramValues) {
        const checked = paramValues[valueId];
        result.push({
          parameterId: paramId,
          valueId: valueId,
          enabled: checked,
        });
      }
    }
    return result;
  };

  const handleSaveChanges = () => {
    if (applicationSceneId && selectedVersionId && scene) {
      editMutation({
        variables: {
          scene: {
            applicationSceneId: applicationSceneId,
            applicationVersionId: selectedVersionId,
            sceneParameterValue: prepareSceneParamsForSave(checkedParams),
          },
        },
      });
    }
  };

  useEffect(() => {
    if (scene) {
      const newCheckedParams = scene?.parameters.reduce((acc, param) => {
        const vals = param.values.reduce(
          (paramVals, currVal) => ({
            ...paramVals,
            [currVal.id]: currVal.enabled,
          }),
          {},
        );
        return { ...acc, [param.id]: vals };
      }, {} as CheckedParam);
      setCheckedParams(newCheckedParams);
      setEdited(false);
    }
  }, [scene, setCheckedParams]);

  const handleParamSelectionChanged =
    (paramId: string, valueId: string) =>
    (e: ChangeEvent, checked: boolean) => {
      setCheckedParams((oldState) => ({
        ...oldState,
        [paramId]: {
          ...oldState[paramId],
          [valueId]: checked,
        },
      }));
      setEdited(true);
    };

  const renderParameter = (param: Parameter) => {
    return (
      <Flex key={param.id} alignItems="baseline">
        <ApplicationSceneGroupParamName>
          {param.name_t}
        </ApplicationSceneGroupParamName>
        <Flex flexGrow={1}>
          {param.values.map((value) => (
            <ApplicationSceneParamValueWrapper key={value.id}>
              <FormControlLabel
                label={value.name_t}
                control={
                  <Checkbox
                    checked={!!checkedParams?.[param.id]?.[value.id]}
                    onChange={handleParamSelectionChanged(param.id, value.id)}
                  />
                }
              />
            </ApplicationSceneParamValueWrapper>
          ))}
        </Flex>
      </Flex>
    );
  };

  return (
    <>
      <TopBar
        leftSideText={scene?.name_t}
        leftSideStatusText={
          <ApplicationSceneStatus enabled={enabled}>
            {enabled
              ? t('applications.scene.enabled', 'Enabled')
              : t('applications.scene.disabled', 'Disabled')}
          </ApplicationSceneStatus>
        }
        actions={
          <>
            <ApplicationVersionSelector
              versions={
                scene?.applicationVersions ? [...scene.applicationVersions] : []
              }
              selectedVersionId={selectedVersionId}
              onVersionSelected={setSelectedVersionId}
            />
            <Button
              id="application-scene-detail-page-edit"
              variant="text"
              startIcon={<EditIcon />}
              onClick={() => setEditDialogOpen(true)}
            >
              {t('applications.scene.editScene', 'Edit scene')}
            </Button>
            <Button
              id="application-scene-detail-page-save"
              variant="contained"
              disabled={!edited}
              startIcon={<SaveIcon />}
              onClick={handleSaveChanges}
            >
              {t('applications.scene.saveChanges', 'Save changes')}
            </Button>
          </>
        }
      />
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <SectionPaper>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                {scene?.imageUrl ? (
                  <img
                    alt={scene?.name_t}
                    src={scene.imageUrl}
                    style={{ maxWidth: '100%' }}
                  />
                ) : (
                  <ImagePlaceholder />
                )}
              </Grid>
              <Grid item xs={8}>
                <Box style={{ marginBottom: theme.spacing(0.5) }}>
                  <BaseTypography variant="body2" uppercase color="gray">
                    {t('applications.scene.description', 'Description')}
                  </BaseTypography>
                </Box>

                <BaseTypography variant="body1">
                  {scene?.description}
                </BaseTypography>
              </Grid>
            </Grid>
          </SectionPaper>

          {(scene?.qrCardColor ||
            scene?.qrLengthMinutes ||
            scene?.qrIconUrl) && (
            <SectionPaper
              title={t('applications.scene.qrPreview', 'QR card preview')}
              style={{ marginTop: theme.spacing(2) }}
            >
              <Flex maxWidth="50ch">
                <QrSceneCard scene={scene} size="small" columns={1} rows={1} />
              </Flex>
            </SectionPaper>
          )}

          <SectionPaper
            title={t('applications.scene.parameters', 'Scene parameters')}
            sx={{ marginTop: 2 }}
          >
            <Flex flexDirection="column">
              {scene?.parameters?.map(renderParameter)}
            </Flex>
          </SectionPaper>

          {scene && selectedVersionId && (
            <ApplicationSceneManualSection
              scene={scene}
              applicationVersionId={selectedVersionId}
            />
          )}
        </>
      )}
      {scene && selectedVersionId && (
        <>
          <ApplicationSceneEditDialog
            scene={scene}
            applicationVersionId={selectedVersionId}
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
          />
        </>
      )}
    </>
  );
};
export default ApplicationSceneDetailPage;

const ApplicationSceneStatus = styled('span', {
  shouldForwardProp: isPropValid,
})<{ enabled: boolean }>(
  ({ enabled, theme: { palette } }) => `
      color: ${enabled ? palette.success.main : palette.error.main};
  `,
);

const ApplicationSceneGroupParamName = styled('div')`
  min-width: 12ch;
  text-transform: uppercase;
  font-weight: bold;
`;

const ApplicationSceneParamValueWrapper = styled(Paper)(
  ({ theme: { spacing } }) => `
    padding-left: ${spacing(2)};
    margin-left: ${spacing(2)};
    margin-top: ${spacing(1)};
    margin-bottom: ${spacing(1)};
`,
);
